// stream-radio-panel.tsx — всплывающий плейлист «Эфир» (стриминг-радио).
//
// Eugene 2026-06-05 Босс: «Эфир» — несколько стрим-станций подключаются в
// ОСНОВНОЙ плеер главной и играют инлайн. Кнопка-триггер рядом с плеером,
// по клику — glass-панель со списком станций. Клик по станции → играет через
// ЕДИНСТВЕННЫЙ persistent-плеер (Persistent-audio-only rule + Single-audio rule).
//
// === Источник станций (gated, no-op пока пусто) ===
// Читается `window.__STREAM_STATIONS__` — JSON-массив, инжектится сервером из
// ENV `STREAM_STATIONS` (см. server/static.ts injectMarketingPlaceholders +
// client/index.html инлайн-скрипт). Доп. build-time фолбэк —
// `import.meta.env.VITE_STREAM_STATIONS`. Если станций 0 → триггер НЕ
// рендерится вовсе (фича выключена, никакого мусора в UI).
//
// === Воспроизведение ===
// Через `loadTrackIntoPlayer(url)` (он же `getPersistentPlayerAudio()` внутри)
// — НЕ создаём new Audio() для станций (нарушит lock-screen + single-audio).
// Live-поток: duration = Infinity/0 → НЕ показываем скраббер, только play/pause
// + LIVE-бейдж. MediaSession metadata задаётся СИНХРОННО в user-gesture
// (Apple-audio-best-practices rule); playbackState на play/pause;
// setPositionState для live НЕ вызываем (по spec). Артворк не задаём через
// cover-helper (у станции нет cover-id) — ставим metadata напрямую.

import { useEffect, useMemo, useRef, useState } from "react";
import { Radio, RadioTower, X, Play, Pause, Loader2 } from "lucide-react";
import {
  loadTrackIntoPlayer,
  getPersistentPlayerAudio,
  setLockScreenPlaybackState,
} from "@/lib/lockscreen";
import { useDraggablePosition } from "@/lib/clampViewport";

interface StreamStation {
  name: string;
  url: string;
  nowPlayingUrl?: string;
  genre?: string;
}

/** Безопасно прочитать массив станций из window + build-time env. */
function readStations(): StreamStation[] {
  const fromWindow = (() => {
    try {
      const w = (window as any).__STREAM_STATIONS__;
      if (Array.isArray(w)) return w;
      if (typeof w === "string" && w.trim()) {
        const parsed = JSON.parse(w);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch {
      /* ignore */
    }
    return null;
  })();
  if (fromWindow && fromWindow.length) return sanitize(fromWindow);

  // Build-time фолбэк (VITE_STREAM_STATIONS — JSON-строка).
  try {
    const raw = (import.meta as any).env?.VITE_STREAM_STATIONS;
    if (typeof raw === "string" && raw.trim()) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return sanitize(parsed);
    }
  } catch {
    /* ignore */
  }
  return fromWindow ? sanitize(fromWindow) : [];
}

function sanitize(arr: any[]): StreamStation[] {
  return arr
    .filter((s) => s && typeof s.url === "string" && s.url.trim())
    .map((s) => ({
      name: typeof s.name === "string" && s.name.trim() ? s.name : "Станция",
      url: String(s.url),
      nowPlayingUrl:
        typeof s.nowPlayingUrl === "string" && s.nowPlayingUrl.trim()
          ? s.nowPlayingUrl
          : undefined,
      genre: typeof s.genre === "string" && s.genre.trim() ? s.genre : undefined,
    }));
}

const PANEL_W = 320;
const PANEL_H = 420;

export default function StreamRadioPanel() {
  const stations = useMemo(() => readStations(), []);
  const [open, setOpen] = useState(false);
  // url станции которая сейчас в эфире (через наш persistent player), либо null
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState<string | null>(null);
  // now-playing текст по url станции
  const [nowPlaying, setNowPlaying] = useState<Record<string, string>>({});

  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Draggable-позиция панели (как другие floating-элементы, clampViewport rule).
  const { pos, onPointerDown, onPointerMove, onPointerUp } = useDraggablePosition(
    "stream-radio-panel-pos",
    () => ({
      x: Math.max(8, (typeof window !== "undefined" ? window.innerWidth : 360) - PANEL_W - 16),
      y: Math.max(8, (typeof window !== "undefined" ? window.innerHeight : 640) - PANEL_H - 96),
    }),
    { width: PANEL_W, height: PANEL_H },
  );

  // Закрытие по клику вне панели (и не по триггеру).
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open]);

  // Синхронизируем activeUrl с реальным состоянием persistent-плеера:
  // если юзер запустил трек плейлиста (другой src) или поставил паузу —
  // станция больше не «в эфире».
  useEffect(() => {
    const audio = (() => {
      try {
        return getPersistentPlayerAudio();
      } catch {
        return null;
      }
    })();
    if (!audio) return;
    const sync = () => {
      try {
        const cur = audio.currentSrc || audio.src || "";
        // если src сменился на не-станцию или audio на паузе — сбрасываем
        if (activeUrl && (audio.paused || (cur && cur !== activeUrl))) {
          setActiveUrl((prev) => {
            const curSrc = audio.currentSrc || audio.src || "";
            if (prev && (audio.paused || (curSrc && curSrc !== prev))) return null;
            return prev;
          });
        }
      } catch {
        /* ignore */
      }
    };
    audio.addEventListener("pause", sync);
    audio.addEventListener("play", sync);
    audio.addEventListener("emptied", sync);
    return () => {
      audio.removeEventListener("pause", sync);
      audio.removeEventListener("play", sync);
      audio.removeEventListener("emptied", sync);
    };
  }, [activeUrl]);

  // Now-playing poll: раз в ~15с для станций с nowPlayingUrl. graceful — при
  // ошибке просто скрываем (не падаем). Только для станции которая в эфире
  // (экономим запросы) + при открытой панели для всех — берём компромисс:
  // поллим ВСЕ станции с nowPlayingUrl пока панель открыта ИЛИ станция активна.
  useEffect(() => {
    const targets = stations.filter((s) => s.nowPlayingUrl && (open || s.url === activeUrl));
    if (!targets.length) return;
    let cancelled = false;

    const poll = async () => {
      for (const st of targets) {
        try {
          const res = await fetch(st.nowPlayingUrl as string, { cache: "no-store" });
          if (!res.ok) throw new Error("bad status");
          const ct = res.headers.get("content-type") || "";
          let title = "";
          if (ct.includes("application/json")) {
            const j: any = await res.json();
            // Частые поля у радио-провайдеров now-playing API.
            title =
              j?.now_playing?.song?.text ||
              j?.nowplaying ||
              j?.title ||
              j?.song ||
              (j?.artist && j?.title ? `${j.artist} — ${j.title}` : "") ||
              "";
          } else {
            title = (await res.text()).trim().slice(0, 160);
          }
          if (!cancelled && title) {
            setNowPlaying((prev) => ({ ...prev, [st.url]: title }));
          }
        } catch {
          if (!cancelled) {
            setNowPlaying((prev) => {
              if (!(st.url in prev)) return prev;
              const next = { ...prev };
              delete next[st.url];
              return next;
            });
          }
        }
      }
    };

    poll();
    const id = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [stations, open, activeUrl]);

  // Фича выключена — станций нет → ничего не рендерим.
  if (!stations.length) return null;

  const playStation = (st: StreamStation) => {
    // Если станция уже в эфире — пауза (toggle).
    const audio = (() => {
      try {
        return getPersistentPlayerAudio();
      } catch {
        return null;
      }
    })();
    if (st.url === activeUrl && audio && !audio.paused) {
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
      setLockScreenPlaybackState("paused");
      setActiveUrl(null);
      return;
    }

    setLoadingUrl(st.url);
    // MediaSession metadata — СИНХРОННО в user-gesture (Apple-audio rule).
    try {
      if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
        navigator.mediaSession.metadata = new (window as any).MediaMetadata({
          title: st.name,
          artist: nowPlaying[st.url] || "Эфир · MuzaAi",
          album: "MuzaAi",
        });
        // Live: только play/pause; убираем скраббер/seek/перемотки.
        const ms = navigator.mediaSession;
        try {
          ms.setActionHandler("play", () => {
            const a = getPersistentPlayerAudio();
            a?.play().then(() => setLockScreenPlaybackState("playing")).catch(() => {});
          });
        } catch {}
        try {
          ms.setActionHandler("pause", () => {
            const a = getPersistentPlayerAudio();
            try {
              a?.pause();
            } catch {}
            setLockScreenPlaybackState("paused");
          });
        } catch {}
        for (const act of ["previoustrack", "nexttrack", "seekto", "seekbackward", "seekforward"] as const) {
          try {
            ms.setActionHandler(act, null);
          } catch {}
        }
      }
    } catch {
      /* старые Safari — игнор */
    }

    // Грузим поток в persistent player (он пауз ит остальное audio через bus).
    const a = loadTrackIntoPlayer(st.url);
    if (!a) {
      setLoadingUrl(null);
      return;
    }
    a.crossOrigin = "anonymous"; // публичные стримы; не требуют cookie
    const onPlaying = () => {
      setLoadingUrl(null);
      setActiveUrl(st.url);
      setLockScreenPlaybackState("playing");
    };
    a.addEventListener("playing", onPlaying, { once: true });
    a.onerror = () => {
      setLoadingUrl(null);
      setActiveUrl(null);
    };
    a.play()
      .then(() => {
        setActiveUrl(st.url);
        setLockScreenPlaybackState("playing");
      })
      .catch(() => {
        setLoadingUrl(null);
      });
  };

  return (
    <>
      {/* Кнопка-триггер «Эфир» — рядом с плеером (внутри #playlist-section).
          z-30 — НИЖЕ Музa-FAB (bottom-right), не конфликтует. */}
      <div className="flex justify-center mt-2 mb-1">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-gradient-to-r from-purple-500 via-fuchsia-500 to-cyan-500 p-[1.5px] shadow-[0_0_18px_rgba(217,70,239,0.30)] active:scale-95 transition-transform"
          aria-label="Открыть Эфир — стриминг-радио"
          data-testid="btn-stream-radio-trigger"
        >
          <span className="inline-flex items-center gap-2 h-full w-full px-3 rounded-full bg-background/85 text-[12px] font-display font-bold text-white">
            {activeUrl ? (
              <RadioTower className="w-4 h-4 text-fuchsia-300 animate-pulse" />
            ) : (
              <Radio className="w-4 h-4 text-cyan-300" />
            )}
            Эфир
            {activeUrl && (
              <span className="inline-flex items-center gap-1 text-[10px] font-sans uppercase tracking-wider text-red-300">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                Live
              </span>
            )}
          </span>
        </button>
      </div>

      {/* Всплывающая glass-панель-плейлист. Draggable + clamp в вьюпорт.
          max-h через dvh + safe-area (Device-fit-100 rule), скролл внутри. */}
      {open && (
        <div
          ref={panelRef}
          className="fixed z-40 glass-card rounded-2xl border border-fuchsia-500/30 shadow-[0_8px_40px_rgba(124,58,237,0.35)] flex flex-col"
          style={{
            left: pos.x,
            top: pos.y,
            width: PANEL_W,
            maxWidth: "calc(100vw - 16px)",
            maxHeight: "min(70dvh, calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))",
          }}
          data-testid="stream-radio-panel"
        >
          {/* Header — drag-handle + заголовок + крестик */}
          <div
            className="flex items-center gap-2 px-4 py-3 border-b border-white/10 cursor-grab active:cursor-grabbing select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            <RadioTower className="w-5 h-5 text-fuchsia-300" />
            <h3 className="flex-1 min-w-0 font-display font-bold text-base bg-gradient-to-r from-purple-300 via-fuchsia-300 to-cyan-300 bg-clip-text text-transparent">
              Эфир
            </h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Закрыть Эфир"
              data-testid="btn-stream-radio-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Список станций — скролл внутри */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {stations.map((st) => {
              const isActive = st.url === activeUrl;
              const isLoading = st.url === loadingUrl;
              const np = nowPlaying[st.url];
              return (
                <button
                  key={st.url}
                  type="button"
                  onClick={() => playStation(st)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                    isActive
                      ? "bg-gradient-to-r from-purple-500/25 via-fuchsia-500/20 to-cyan-500/15 border border-fuchsia-400/40"
                      : "bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.07] hover:border-fuchsia-400/30"
                  }`}
                  data-testid="stream-station-row"
                >
                  <span className="shrink-0 w-9 h-9 rounded-full bg-black/40 border border-white/10 flex items-center justify-center">
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 text-cyan-300 animate-spin" />
                    ) : isActive ? (
                      <Pause className="w-4 h-4 text-fuchsia-300" />
                    ) : (
                      <Play className="w-4 h-4 text-cyan-300" />
                    )}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="flex-1 min-w-0 truncate font-sans font-medium text-[13px] text-white/90">
                        {st.name}
                      </span>
                      {isActive && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[9px] font-sans uppercase tracking-wider text-red-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                          Live
                        </span>
                      )}
                    </span>
                    {st.genre && (
                      <span className="block truncate text-[11px] font-sans text-fuchsia-300/70">
                        {st.genre}
                      </span>
                    )}
                    {np && (
                      <span className="block truncate text-[11px] font-sans text-white/50">
                        ♪ {np}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
